import React, { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { apiRequest } from '../../lib/queryClient';

interface EmissionEntry {
  id: number;
  scope: string;
  activity_type: string;
  activity_description?: string;
  quantity: number;
  unit: string;
  emission_factor?: number;
  emission_factor_source?: string;
  co2_equivalent: number;
  calculation_formula?: string;
  status: string;
  is_estimated: boolean;
  estimation_method?: string;
  created_at: string;
  reporting_year?: number;
}

interface Scope1EntriesTableProps {
  entries: EmissionEntry[];
  isLoading: boolean;
}

type FilterType = 'all' | 'measured' | 'scenario1' | 'scenario2';
type SortField = 'co2_equivalent' | 'activity_type';
type SortOrder = 'asc' | 'desc';

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

export function Scope1EntriesTable({ entries, isLoading }: Scope1EntriesTableProps) {
  const queryClient = useQueryClient();
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [sortField, setSortField] = useState<SortField>('co2_equivalent');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const res = await apiRequest('POST', `/api/emissions/${id}/status`, { status });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/emissions'] });
    },
    onError: (err: Error) => {
      alert(err.message || 'Status update failed');
    },
  });

  const filteredAndSortedEntries = useMemo(() => {
    let filtered = entries;

    if (filterType !== 'all') {
      filtered = filtered.filter(entry => {
        switch (filterType) {
          case 'measured':
            return !entry.is_estimated;
          case 'scenario1':
            return entry.is_estimated && entry.estimation_method === 'vehicle_based';
          case 'scenario2':
            return entry.is_estimated && entry.estimation_method === 'fuel_price_based';
          default:
            return true;
        }
      });
    }

    if (searchTerm) {
      filtered = filtered.filter(entry =>
        entry.activity_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.activity_description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    filtered = [...filtered].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === 'co2_equivalent') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      } else {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [entries, filterType, sortField, sortOrder, searchTerm]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const getEntryType = (entry: EmissionEntry) => {
    if (!entry.is_estimated) return 'Measured';
    if (entry.estimation_method === 'vehicle_based') return 'Scenario 1';
    if (entry.estimation_method === 'fuel_price_based') return 'Scenario 2';
    return 'Estimated';
  };

  const getEntryTypeColor = (entry: EmissionEntry) => {
    if (!entry.is_estimated) return 'bg-green-100 text-green-800';
    if (entry.estimation_method === 'vehicle_based') return 'bg-blue-100 text-blue-800';
    if (entry.estimation_method === 'fuel_price_based') return 'bg-purple-100 text-purple-800';
    return 'bg-gray-100 text-gray-800';
  };

  const lineageText = (entry: EmissionEntry) => {
    if (entry.calculation_formula) return entry.calculation_formula;
    if (entry.emission_factor != null) {
      const src = entry.emission_factor_source ? ` [${entry.emission_factor_source}]` : '';
      return `${entry.quantity} ${entry.unit} × ${entry.emission_factor}${src} = ${entry.co2_equivalent} kg CO₂e`;
    }
    return 'Calculation details unavailable';
  };

  const nextActions = (status: string) => {
    const s = (status || 'draft').toLowerCase();
    if (s === 'draft') return [{ label: 'Submit', status: 'submitted' }];
    if (s === 'submitted') return [
      { label: 'Approve', status: 'approved' },
      { label: 'Reject', status: 'rejected' },
    ];
    if (s === 'rejected') return [{ label: 'Reopen', status: 'draft' }];
    if (s === 'approved') return [{ label: 'Reopen', status: 'draft' }];
    return [];
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Existing Scope 1 Entries</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-gray-500 text-center py-8">Loading entries...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Existing Scope 1 Entries</span>
          <span className="text-sm font-normal text-gray-500">
            {filteredAndSortedEntries.length} of {entries.length} entries
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-6 space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search by activity type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="min-w-48">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as FilterType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="measured">Measured</option>
                <option value="scenario1">Scenario 1 (Vehicle-based)</option>
                <option value="scenario2">Scenario 2 (Fuel Price-based)</option>
              </select>
            </div>
          </div>
        </div>

        {filteredAndSortedEntries.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th
                    className="border border-gray-200 px-4 py-3 text-left cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('activity_type')}
                  >
                    Activity Type {getSortIcon('activity_type')}
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-left">Type</th>
                  <th className="border border-gray-200 px-4 py-3 text-left">Status</th>
                  <th className="border border-gray-200 px-4 py-3 text-right">Quantity</th>
                  <th
                    className="border border-gray-200 px-4 py-3 text-right cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('co2_equivalent')}
                  >
                    CO₂e (kg) {getSortIcon('co2_equivalent')}
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedEntries.map((entry) => (
                  <React.Fragment key={entry.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="border border-gray-200 px-4 py-3">
                        <button
                          type="button"
                          className="font-medium text-gray-900 text-left hover:underline"
                          onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                        >
                          {entry.activity_type || 'Unknown Activity'}
                        </button>
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEntryTypeColor(entry)}`}>
                          {getEntryType(entry)}
                        </span>
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${STATUS_COLORS[(entry.status || 'draft').toLowerCase()] || STATUS_COLORS.draft}`}>
                          {entry.status || 'draft'}
                        </span>
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-right">
                        {entry.quantity?.toLocaleString() || '0'} {entry.unit || ''}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-right">
                        <div className="font-semibold text-green-700">
                          {entry.co2_equivalent?.toFixed(2) || '0.00'} kg
                        </div>
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {nextActions(entry.status).map((action) => (
                            <Button
                              key={action.status}
                              size="sm"
                              variant="outline"
                              disabled={statusMutation.isPending}
                              onClick={() => statusMutation.mutate({ id: entry.id, status: action.status })}
                            >
                              {action.label}
                            </Button>
                          ))}
                        </div>
                      </td>
                    </tr>
                    {expandedId === entry.id && (
                      <tr className="bg-green-50">
                        <td colSpan={6} className="border border-gray-200 px-4 py-3 text-sm text-gray-700">
                          <div className="font-medium text-green-900 mb-1">Calculation lineage</div>
                          <code className="text-xs break-all">{lineageText(entry)}</code>
                          {entry.emission_factor_source && (
                            <div className="mt-1 text-xs text-gray-500">
                              Factor source: {entry.emission_factor_source}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {searchTerm || filterType !== 'all'
              ? 'No entries match your current filters.'
              : 'No entries found. Add your first Scope 1 emission entry.'}
          </div>
        )}

        {filteredAndSortedEntries.length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Total CO₂e:{' '}
                <span className="font-semibold text-green-700">
                  {filteredAndSortedEntries.reduce((sum, entry) => sum + (entry.co2_equivalent || 0), 0).toFixed(2)} kg
                </span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
